import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BrandMark } from "../components/Brand";
import StatusBadge from "../components/StatusBadge";
import SignaturePad from "../components/SignaturePad";
import { api, formatApiError, formatCurrency, formatDate } from "../lib/api";
import { FileText, Upload, ShieldCheck, CheckCircle2, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Reframed: optional / "if available" — never gates the workflow.
const DOC_CATEGORIES = [
  { v: "police_report", l: "Police report" },
  { v: "insurance_dec", l: "Insurance Dec Sheet / claim # / limits" },
  { v: "medical_records", l: "Medical records (ER, MRI, narrative, op reports)" },
  { v: "hospital_admit", l: "Hospital admit / discharge summary" },
  { v: "complaint", l: "Complaint" },
  { v: "demand_letter", l: "Demand / offer letter" },
  { v: "other", l: "Other" },
];

const LIABILITY_OPTIONS = [
  { v: "accepted", l: "Accepted by insurer" },
  { v: "denied", l: "Denied" },
  { v: "partially_accepted", l: "Partially accepted" },
  { v: "unclear", l: "Unclear / under investigation" },
];

const COMPARATIVE_OPTIONS = [
  { v: "none", l: "None — clear liability against defendant" },
  { v: "minor", l: "Minor (< 25%)" },
  { v: "significant", l: "Significant (25–50%)" },
  { v: "unsure", l: "Unsure" },
];

const WC_CLAIM_STATUS = [
  { v: "accepted",  l: "Accepted" },
  { v: "disputed",  l: "Disputed" },
  { v: "pending",   l: "Pending" },
];

const WC_BENEFIT_STATUS = [
  { v: "receiving",  l: "Receiving benefits" },
  { v: "suspended",  l: "Benefits suspended" },
  { v: "denied",     l: "Benefits denied" },
  { v: "unknown",    l: "Unknown" },
];

const WC_WORK_STATUS = [
  { v: "full_duty",   l: "Working full duty" },
  { v: "light_duty",  l: "Working light duty" },
  { v: "not_working", l: "Not working" },
];

export default function AttorneyPortal() {
  const { token } = useParams();
  const [app, setApp] = useState(null);
  const [error, setError] = useState(null);
  const [docName, setDocName] = useState("");
  const [docCat, setDocCat] = useState("medical_records");
  const [note, setNote] = useState("");
  const [uw, setUw] = useState({});
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/by-token/${token}`);
      setApp(data);
      setUw({
        case_type: data.case_type || "auto_accident",
        employer_name: data.employer_name ?? "",
        case_value_estimate: data.case_value_estimate ?? "",
        defendant_carrier: data.defendant_carrier ?? "",
        defendant_policy_limits: data.defendant_policy_limits ?? "",
        liability_status: data.liability_status ?? "",
        liability_theory: data.liability_theory ?? "",
        plaintiff_age: data.plaintiff_age ?? "",
        co_plaintiffs: data.co_plaintiffs ?? "",
        diagnoses_treatment: data.diagnoses_treatment ?? "",
        mri_findings: data.mri_findings ?? "",
        surgeries: data.surgeries ?? "",
        comparative_negligence: data.comparative_negligence ?? "",
        complaint_filed: data.complaint_filed ?? null,
        complaint_filed_date: data.complaint_filed_date ?? "",
        case_strength_notes: data.case_strength_notes ?? "",
        // Workers' comp specific
        wc_claim_status: data.wc_claim_status ?? "",
        wc_benefit_status: data.wc_benefit_status ?? "",
        wc_work_status: data.wc_work_status ?? "",
        wc_additional_comments: data.wc_additional_comments ?? "",
      });
    } catch { setError("This link is invalid or has expired."); }
  };

  const setU = (k) => (v) => setUw((u) => ({ ...u, [k]: v?.target ? v.target.value : v }));

  const uploadDoc = async () => {
    if (!docName.trim()) return;
    await api.post(`/applications/${app.id}/documents`, {
      name: docName.trim().includes(".") ? docName.trim() : `${docName.trim()}.pdf`,
      category: docCat,
      uploaded_by: "attorney",
      size_kb: Math.floor(Math.random() * 1500) + 300,
    });
    setDocName("");
    toast.success("Document added");
    load();
  };

  const removeDoc = async (id) => {
    await api.delete(`/applications/${app.id}/documents/${id}`);
    load();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/applications/${app.id}/notes`, { author: app.attorney_name, role: "attorney", content: note.trim() });
    setNote("");
    toast.success("Note submitted");
    load();
  };

  // Coerce numeric-ish strings to numbers; keep nulls/empty out of payload.
  const buildUwPayload = () => {
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const str = (v) => (v === "" || v === null || v === undefined ? null : String(v).trim());
    const intv = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));
    return {
      case_type: str(uw.case_type),
      employer_name: str(uw.employer_name),
      case_value_estimate: num(uw.case_value_estimate),
      defendant_carrier: str(uw.defendant_carrier),
      defendant_policy_limits: num(uw.defendant_policy_limits),
      liability_status: str(uw.liability_status),
      liability_theory: str(uw.liability_theory),
      plaintiff_age: intv(uw.plaintiff_age),
      co_plaintiffs: intv(uw.co_plaintiffs),
      diagnoses_treatment: str(uw.diagnoses_treatment),
      mri_findings: str(uw.mri_findings),
      surgeries: str(uw.surgeries),
      comparative_negligence: str(uw.comparative_negligence),
      complaint_filed: typeof uw.complaint_filed === "boolean" ? uw.complaint_filed : null,
      complaint_filed_date: str(uw.complaint_filed_date),
      case_strength_notes: str(uw.case_strength_notes),
      wc_claim_status: str(uw.wc_claim_status),
      wc_benefit_status: str(uw.wc_benefit_status),
      wc_work_status: str(uw.wc_work_status),
      wc_additional_comments: str(uw.wc_additional_comments),
    };
  };

  const saveUnderwriting = async () => {
    setSaving(true);
    try {
      const payload = buildUwPayload();
      // Strip nulls so the backend's "no fields to update" guard doesn't trip
      const cleaned = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== ""));
      if (!Object.keys(cleaned).length) { toast.info("Add at least one field before saving"); return; }
      await api.patch(`/applications/by-token/${token}/underwriting`, cleaned);
      toast.success("Underwriting answers saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitSignature = async (payload) => {
    setSigning(true);
    try {
      await api.post(`/applications/${app.id}/signature`, payload);
      toast.success("Representation verified");
      load();
    } finally { setSigning(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-6">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 max-w-md text-center">
        <div className="w-14 h-14 rounded-full bg-[#FEF2F2] border border-[#FECACA] flex items-center justify-center mx-auto mb-4 text-[#B91C1C]">!</div>
        <h2 className="font-display text-xl text-[#0A2540] font-semibold">Link unavailable</h2>
        <p className="text-[#6B7280] mt-2 text-sm">{error}</p>
      </div>
    </div>
  );
  if (!app) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="attorney-portal">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <BrandMark />
          <div className="flex items-center gap-2 text-xs text-[#6B7280]"><ShieldCheck className="w-4 h-4 text-[#0066FF]" /> Secure attorney portal</div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10">
        <div className="bg-[#0A2540] text-white rounded-2xl p-8 mb-6 relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-[#0066FF]/30 blur-3xl" />
          <div className="relative">
            <div className="uppercase-label text-white/60 mb-2">Verification request</div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">{app.name}</h1>
            <div className="text-white/70 mt-2 text-sm">Submitted {formatDate(app.created_at)} · {app.accident_type}</div>
            <div className="mt-6 flex flex-wrap gap-6">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/60">Funding requested</div>
                <div className="font-display text-3xl font-semibold mt-1">{formatCurrency(app.requested_amount)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/60">Status</div>
                <div className="mt-2"><StatusBadge status={app.status} /></div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/60">Verification</div>
                <div className="mt-2 flex items-center gap-1.5 text-sm">
                  {app.attorney_verified ? <><CheckCircle2 className="w-4 h-4 text-[#6EE7B7]" /> Verified</> : "Pending signature"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Case summary */}
          <Card title="Case summary">
            <KV label="Accident date" value={formatDate(app.accident_date)} />
            <KV label="Phone" value={app.phone} />
            <KV label="Email" value={app.email} />
            <div>
              <div className="uppercase-label mb-1">Injury description (from claimant)</div>
              <p className="text-sm text-[#374151] leading-relaxed">{app.injury_description}</p>
            </div>
          </Card>

          {/* Verify representation */}
          <Card title={app.attorney_verified ? "Representation verified" : "Verify representation"}>
            {app.attorney_verified ? (
              <div className="space-y-3">
                {app.signature_data && <img src={app.signature_data} alt="signature" className="border border-[#E5E7EB] rounded p-2 bg-[#F9FAFB] max-h-32" />}
                <div className="text-sm text-[#0A2540] font-medium">{app.signature_name}</div>
                <div className="text-xs text-[#6B7280]">Thank you for verifying. Underwriting will contact you with any further questions.</div>
              </div>
            ) : (
              <SignaturePad onSubmit={submitSignature} submitting={signing} />
            )}
          </Card>

          {/* Underwriting questions — full width */}
          <Card title="Underwriting questions" className="lg:col-span-2"
            sub="Quick answers help us approve faster and at a higher amount. Save what you have — you can come back and finish later.">
            {/* Case Type dropdown — drives which question set renders.  Editable here
                so paralegals/attorneys can override the case type if intake got it wrong. */}
            <div className="mb-6 p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[260px]">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Case Type</Label>
                <Select value={uw.case_type || "auto_accident"} onValueChange={setU("case_type")}>
                  <SelectTrigger className="mt-1.5" data-testid="uw-case-type">
                    <SelectValue placeholder="Select case type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto_accident">Auto Accident</SelectItem>
                    <SelectItem value="workers_comp">Workers&apos; Compensation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-[#6B7280] max-w-md">
                Changing this swaps the underwriting questions below. Your answers in either set are preserved if you switch back.
              </div>
            </div>

            {uw.case_type === "workers_comp" ? (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="uw-wc-fields">
                <div>
                  <Label className="text-sm">Date of injury</Label>
                  <Input type="date" value={app.accident_date || ""} disabled className="mt-1.5 bg-[#F9FAFB]" data-testid="uw-wc-date" />
                  <div className="text-[11px] text-[#9CA3AF] mt-1">From plaintiff intake.</div>
                </div>
                <div>
                  <Label className="text-sm">Employer</Label>
                  <Input placeholder="e.g. Acme Logistics, Inc." value={uw.employer_name} onChange={setU("employer_name")} className="mt-1.5" data-testid="uw-wc-employer" />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-sm">Injury description</Label>
                  <Textarea rows={2} placeholder="What happened, mechanism of injury, scene context…" value={uw.diagnoses_treatment} onChange={setU("diagnoses_treatment")} className="mt-1.5" data-testid="uw-wc-injury-desc" />
                </div>

                <div>
                  <Label className="text-sm">Accepted or disputed claim?</Label>
                  <Select value={uw.wc_claim_status || ""} onValueChange={setU("wc_claim_status")}>
                    <SelectTrigger className="mt-1.5" data-testid="uw-wc-claim-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{WC_CLAIM_STATUS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm">Current benefit status</Label>
                  <Select value={uw.wc_benefit_status || ""} onValueChange={setU("wc_benefit_status")}>
                    <SelectTrigger className="mt-1.5" data-testid="uw-wc-benefit-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{WC_BENEFIT_STATUS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-sm">Diagnoses & treatment to date</Label>
                  <Textarea rows={2} placeholder="ICD-10 codes, current providers, PT/chiro/injections, work-up status." value={uw.mri_findings} onChange={setU("mri_findings")} className="mt-1.5" data-testid="uw-wc-diagnoses" />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-sm">Surgeries performed or recommended</Label>
                  <Input placeholder="e.g. L4-L5 fusion recommended; lumbar epidural 11/14/2025" value={uw.surgeries} onChange={setU("surgeries")} className="mt-1.5" data-testid="uw-wc-surgeries" />
                </div>

                <div>
                  <Label className="text-sm">Current work status</Label>
                  <Select value={uw.wc_work_status || ""} onValueChange={setU("wc_work_status")}>
                    <SelectTrigger className="mt-1.5" data-testid="uw-wc-work-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{WC_WORK_STATUS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm">Estimated settlement / value exposure</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span>
                    <Input type="number" placeholder="75000" value={uw.case_value_estimate} onChange={setU("case_value_estimate")} className="pl-7" data-testid="uw-wc-value" />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-sm">Overall thoughts on case strength</Label>
                  <Textarea rows={3} placeholder="Liability questions, employer cooperation, IME concerns, MMI status, sympathetic worker…" value={uw.case_strength_notes} onChange={setU("case_strength_notes")} className="mt-1.5" data-testid="uw-wc-strength" />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-sm">Additional underwriting comments</Label>
                  <Textarea rows={3} placeholder="Anything else underwriting should know — prior WC claims, structured settlement preference, hearing date, mediation outcome…" value={uw.wc_additional_comments} onChange={setU("wc_additional_comments")} className="mt-1.5" data-testid="uw-wc-comments" />
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="uw-mva-fields">
              <div className="sm:col-span-2">
                <Label className="text-sm">Liability theory of the case</Label>
                <Textarea rows={2} placeholder="e.g. Defendant rear-ended plaintiff at red light; clear breach of duty…" value={uw.liability_theory} onChange={setU("liability_theory")} className="mt-1.5" data-testid="uw-liability-theory" />
              </div>

              <div>
                <Label className="text-sm">Has liability been accepted or denied?</Label>
                <Select value={uw.liability_status || ""} onValueChange={setU("liability_status")}>
                  <SelectTrigger className="mt-1.5" data-testid="uw-liability-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{LIABILITY_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Comparative negligence concerns</Label>
                <Select value={uw.comparative_negligence || ""} onValueChange={setU("comparative_negligence")}>
                  <SelectTrigger className="mt-1.5" data-testid="uw-comparative"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{COMPARATIVE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Defendant&apos;s insurance carrier</Label>
                <Input placeholder="e.g. State Farm" value={uw.defendant_carrier} onChange={setU("defendant_carrier")} className="mt-1.5" data-testid="uw-carrier" />
              </div>

              <div>
                <Label className="text-sm">Policy limits (USD)</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span>
                  <Input type="number" placeholder="100000" value={uw.defendant_policy_limits} onChange={setU("defendant_policy_limits")} className="pl-7" data-testid="uw-limits" />
                </div>
              </div>

              <div>
                <Label className="text-sm">Plaintiff&apos;s age</Label>
                <Input type="number" placeholder="35" value={uw.plaintiff_age} onChange={setU("plaintiff_age")} className="mt-1.5" data-testid="uw-age" />
              </div>

              <div>
                <Label className="text-sm">Number of plaintiffs</Label>
                <Input type="number" placeholder="1" value={uw.co_plaintiffs} onChange={setU("co_plaintiffs")} className="mt-1.5" data-testid="uw-co-plaintiffs" />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-sm">Diagnoses & treatment to date</Label>
                <Textarea rows={2} placeholder="e.g. Whiplash, lumbar strain. ER + 6 weeks PT, ongoing chiropractic care." value={uw.diagnoses_treatment} onChange={setU("diagnoses_treatment")} className="mt-1.5" data-testid="uw-diagnoses" />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-sm">MRI findings (if applicable)</Label>
                <Textarea rows={2} placeholder="e.g. L4-L5 disc bulge, no stenosis, mild facet arthropathy." value={uw.mri_findings} onChange={setU("mri_findings")} className="mt-1.5" data-testid="uw-mri" />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-sm">Accident-related surgeries (if any)</Label>
                <Input placeholder="e.g. Microdiscectomy 11/12/2025 — Dr. Patel" value={uw.surgeries} onChange={setU("surgeries")} className="mt-1.5" data-testid="uw-surgeries" />
              </div>

              <div>
                <Label className="text-sm">Has a complaint been filed?</Label>
                <Select
                  value={uw.complaint_filed === true ? "yes" : uw.complaint_filed === false ? "no" : ""}
                  onValueChange={(v) => setUw((u) => ({ ...u, complaint_filed: v === "yes" ? true : v === "no" ? false : null }))}
                >
                  <SelectTrigger className="mt-1.5" data-testid="uw-complaint-filed"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">Not yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Filing date (if filed)</Label>
                <Input type="date" value={uw.complaint_filed_date} onChange={setU("complaint_filed_date")} className="mt-1.5" data-testid="uw-complaint-date" />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-sm">Estimated case value <span className="text-[#9CA3AF]">(rough estimate is fine)</span></Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span>
                  <Input type="number" placeholder="75000" value={uw.case_value_estimate} onChange={setU("case_value_estimate")} className="pl-7" data-testid="uw-case-value" />
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label className="text-sm">Your overall thoughts on case strength</Label>
                <Textarea rows={3} placeholder="Anything we should know — defenses raised, prior settlements, jurisdiction quirks, sympathetic plaintiff…" value={uw.case_strength_notes} onChange={setU("case_strength_notes")} className="mt-1.5" data-testid="uw-strength-notes" />
              </div>
            </div>
            )}
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-[#E5E7EB]">
              <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                <Sparkles className="w-3.5 h-3.5 text-[#0066FF]" /> All fields optional. Save anytime.
              </div>
              <Button onClick={saveUnderwriting} disabled={saving} className="btn-primary-navy" data-testid="save-underwriting-btn">
                {saving ? "Saving…" : "Save underwriting answers"}
              </Button>
            </div>
          </Card>

          {/* Documents — reframed as helpful, not required */}
          <Card title="Helpful documents (if available)" sub="Don't have these handy? No problem — skip and submit later. Doesn't slow approval.">
            <div className="space-y-3">
              {app.documents.length === 0 && <div className="text-sm text-[#6B7280] italic">No documents uploaded yet.</div>}
              {app.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 border border-[#E5E7EB] rounded-lg" data-testid={`atty-doc-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0A2540] truncate">{d.name}</div>
                      <div className="text-xs text-[#6B7280]">{d.category.replace("_", " ")} · {(d.size_kb / 1024).toFixed(2)} MB</div>
                    </div>
                  </div>
                  {d.uploaded_by === "attorney" && (
                    <Button variant="ghost" size="sm" onClick={() => removeDoc(d.id)} data-testid={`atty-del-doc-${d.id}`}><Trash2 className="w-4 h-4 text-[#B91C1C]" /></Button>
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-2 pt-3 mt-3 border-t border-[#E5E7EB]">
              <Input placeholder="File name (e.g., police_report.pdf)" value={docName} onChange={(e) => setDocName(e.target.value)} data-testid="atty-doc-name" />
              <div className="flex gap-2">
                <Select value={docCat} onValueChange={setDocCat}>
                  <SelectTrigger className="flex-1" data-testid="atty-doc-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={uploadDoc} className="btn-primary-navy" data-testid="atty-upload-btn"><Upload className="w-4 h-4 mr-2" />Add</Button>
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card title="Notes for the underwriter">
            <div className="space-y-3 mb-3">
              {app.notes.length === 0 && <div className="text-sm text-[#6B7280] italic">No notes yet.</div>}
              {app.notes.map((n) => (
                <div key={n.id} className="p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                  <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1.5">
                    <span className="font-medium text-[#0A2540]">{n.author} · {n.role}</span>
                    <span>{formatDate(n.created_at)}</span>
                  </div>
                  <div className="text-sm text-[#374151] whitespace-pre-wrap">{n.content}</div>
                </div>
              ))}
            </div>
            <Textarea placeholder="Add a note…" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="atty-note-input" />
            <Button onClick={addNote} className="btn-primary-navy mt-2" data-testid="atty-add-note">Add note</Button>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Card = ({ title, sub, children, className = "" }) => (
  <div className={`bg-white border border-[#E5E7EB] rounded-2xl p-6 ${className}`}>
    <h3 className="font-display text-lg font-semibold text-[#0A2540]">{title}</h3>
    {sub && <p className="text-xs text-[#6B7280] mt-1 mb-4 leading-relaxed">{sub}</p>}
    {!sub && <div className="mb-4" />}
    <div className="space-y-3">{children}</div>
  </div>
);

const KV = ({ label, value }) => (
  <div>
    <div className="uppercase-label mb-0.5">{label}</div>
    <div className="text-sm text-[#0A2540]">{value}</div>
  </div>
);
